import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip, useMap } from 'react-leaflet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select } from '@/components/ui/input';
import { StatCard } from '@/components/ui/stat-card';
import { oceanographyService, BACKEND_ROOT_URL } from '@/services/api';
import { erddapService, ERDDAPDataPoint } from '@/services/erddapService';
import { copernicusService } from '@/services/copernicusService';

import {
  DataSourceBadge,
  EnhancedLegend,
  HeatmapLayer,
  GriddedHeatmapLayer,
  LayerControl,
  NASAOceanColorLayer,
  ArgoFloatLayer,
  DataSourceMode,
  VisibleLayer,
  TemporalAnimationControl,
  TimeFrame,
} from '@/components/oceanography';
import {
  Map as MapIcon, Layers, Thermometer, Droplets, Wind,
  Download, RefreshCw, MapPin, Eye, EyeOff,
  Waves, Loader, Satellite, ChevronDown, ChevronUp, Database, Fish
} from 'lucide-react';
import { cn } from '@/lib/utils';
import 'leaflet/dist/leaflet.css';

const MAP_LAYERS = [
  { id: 'temperature', name: 'Sea Surface Temperature', icon: Thermometer, color: 'coral', enabled: true },
  { id: 'salinity', name: 'Salinity', icon: Droplets, color: 'ocean', enabled: false },
  { id: 'chlorophyll', name: 'Chlorophyll', icon: Waves, color: 'marine', enabled: false },
  { id: 'dissolved_oxygen', name: 'Dissolved Oxygen', icon: Wind, color: 'deep', enabled: false },
  { id: 'pH', name: 'pH Level', icon: MapPin, color: 'abyss', enabled: false },
  { id: 'fisheries_cpue', name: 'Fisheries CPUE', icon: Fish, color: 'coral', enabled: false },
];

// Helper function to safely format numbers
const formatNumber = (value: any, decimals: number = 2): string => {
  if (value === null || value === undefined) return '—';
  const num = typeof value === 'number' ? value : parseFloat(value);
  return isNaN(num) ? String(value) : num.toFixed(decimals);
};

// Color scale for temperature
const getTemperatureColor = (value: number) => {
  const num = typeof value === 'number' ? value : parseFloat(value) || 0;
  if (num < 18) return '#3b82f6'; // blue
  if (num < 22) return '#22c55e'; // green
  if (num < 26) return '#eab308'; // yellow
  if (num < 30) return '#f97316'; // orange
  return '#ef4444'; // red
};

// Color scale for other parameters
const getParameterColor = (param: string, value: number) => {
  const num = typeof value === 'number' ? value : parseFloat(value as any) || 0;
  if (param === 'temperature') return getTemperatureColor(num);
  if (param === 'salinity') {
    if (num < 33) return '#60a5fa';
    if (num < 35) return '#3b82f6';
    return '#1d4ed8';
  }
  // Chlorophyll log-scale color (0.01-50 mg/m³ range, log-distributed)
  // Standard ocean color palette: Blue → Green → Yellow → Orange → Red
  if (param === 'chlorophyll') {
    // Apply log10 scaling for better visualization of orders of magnitude
    const logVal = num > 0.01 ? Math.log10(num) : -2; // log10(0.01) = -2
    // Map log range [-2, 1.7] (0.01 to 50) to color scale
    // -2 = oligotrophic (blue), -0.5 = ~0.3 mg/m³ (green), 0.5 = ~3 mg/m³ (yellow), 1.5 = ~30 mg/m³ (red)
    if (logVal < -1) return '#3b82f6';    // Blue - very low (oligotrophic)
    if (logVal < -0.3) return '#06b6d4';  // Cyan - low
    if (logVal < 0) return '#22c55e';     // Green - moderate (~0.5-1 mg/m³)
    if (logVal < 0.5) return '#84cc16';   // Lime - mesotrophic
    if (logVal < 1) return '#eab308';     // Yellow - productive
    if (logVal < 1.3) return '#f97316';   // Orange - high bloom
    return '#ef4444';                      // Red - extreme bloom (>20 mg/m³)
  }
  // pH color scale - perceptually uniform palette (orange → teal → blue)
  // Tight range for surface ocean: 7.95-8.15
  if (param === 'pH' || param === 'ph') {
    if (num < 7.9) return '#ef4444';      // Red - acidifying (warning)
    if (num < 7.98) return '#fb923c';     // Light orange - low end
    if (num < 8.03) return '#fbbf24';     // Amber - below normal
    if (num < 8.08) return '#14b8a6';     // Teal - normal (~8.05)
    if (num < 8.12) return '#06b6d4';     // Cyan - slightly alkaline
    return '#3b82f6';                      // Blue - alkaline
  }
  // Dissolved Oxygen color scale (4-8 mg/L typical range)
  if (param === 'dissolved_oxygen') {
    if (num < 4) return '#ef4444';       // Red - hypoxic (warning)
    if (num < 5) return '#f97316';       // Orange - low
    if (num < 6) return '#fbbf24';       // Amber - marginal
    if (num < 7) return '#22c55e';       // Green - good
    return '#3b82f6';                     // Blue - excellent
  }
  return '#0ea5e9';
};

// Color scale for CPUE (catch per unit effort) - blue to red gradient
const getCpueColor = (cpue: number, maxCpue: number = 20) => {
  const normalized = Math.min(cpue / maxCpue, 1);
  if (normalized < 0.25) return '#3b82f6'; // blue - low catch
  if (normalized < 0.5) return '#22c55e';  // green
  if (normalized < 0.75) return '#f97316'; // orange
  return '#ef4444'; // red - high catch
};

// Map center control component
function MapCenterControl({ center }: { center: [number, number] }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, map]);
  return null;
}

// Collapsible Panel Component
function CollapsiblePanel({
  title,
  icon: Icon,
  children,
  defaultExpanded = true,
  className
}: {
  title: string;
  icon: any;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  className?: string;
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <Card variant="default" className={className}>
      <CardHeader
        className="pb-3 cursor-pointer hover:bg-gray-50/50 transition-colors rounded-t-xl"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Icon className="w-4 h-4 text-ocean-500" />
            {title}
          </CardTitle>
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent className="space-y-2 pt-0">
          {children}
        </CardContent>
      )}
    </Card>
  );
}

// Zoom change handler component - tracks zoom level for resolution scaling
function ZoomChangeHandler({ onZoomChange }: { onZoomChange: (zoom: number) => void }) {
  const map = useMap();
  useEffect(() => {
    const handleZoomEnd = () => {
      onZoomChange(map.getZoom());
    };
    map.on('zoomend', handleZoomEnd);
    // Set initial zoom
    onZoomChange(map.getZoom());
    return () => {
      map.off('zoomend', handleZoomEnd);
    };
  }, [map, onZoomChange]);
  return null;
}

export default function OceanographyViewer() {
  const navigate = useNavigate();
  const location = useLocation();
  const [layers, setLayers] = useState(MAP_LAYERS);
  const [selectedParameter, setSelectedParameter] = useState('temperature');
  const [selectedPoint, setSelectedPoint] = useState<any>(null);
  const [mapCenter, setMapCenter] = useState<[number, number]>([10, 76]); // Indian Ocean
  const [region, setRegion] = useState('indian');

  // NEW: Data source and layer controls
  const [dataSourceMode, setDataSourceMode] = useState<DataSourceMode>('erddap');
  const [visibleLayers, setVisibleLayers] = useState<VisibleLayer[]>(['markers']);

  // Handle navigation from other pages (e.g. Fisheries Stock Tab)
  useEffect(() => {
    if (location.state) {
      const { parameter, center, zoom, mode } = location.state as any;

      if (mode) setDataSourceMode(mode);
      if (parameter) {
        setSelectedParameter(parameter);
        setLayers(prev => prev.map(l => ({
          ...l,
          enabled: l.id === parameter
        })));
      }
      if (center) setMapCenter(center);
      // Zoom is handled by MapContainer or initial state, MapCenterControl should update it if needed. 
      // If we really need zoom update we might need to expose it to MapCenterControl or use a separate effect.
      // But for now let's just set center.
    }
  }, [location.state]);

  // Filter states
  const [depthRange, setDepthRange] = useState<[number, number]>([0, 500]);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
  const [selectedSource, setSelectedSource] = useState('');

  // NEW: Zoom level for dynamic resolution scaling
  const [zoomLevel, setZoomLevel] = useState(5);

  // NEW: Temporal animation state
  const [animationFrameIndex, setAnimationFrameIndex] = useState(0);
  const [animationFrames, setAnimationFrames] = useState<TimeFrame[]>([]);

  // Calculate stride based on zoom level (professional platforms do this)
  // Lower zoom = higher stride (less data), Higher zoom = lower stride (more data)
  const getStrideForZoom = useCallback((zoom: number) => {
    if (zoom >= 8) return 2;   // Full resolution at high zoom
    if (zoom >= 7) return 5;   // High detail
    if (zoom >= 6) return 10;  // Medium detail
    if (zoom >= 5) return 15;  // Standard view
    return 25;                  // Overview - maximum sampling
  }, []);

  // Calculate estimated total grid cells (for Indian Ocean bounds: -15 to 25 lat, 50 to 100 lon)
  const estimateTotalGridCells = useCallback((parameter: string) => {
    // Different datasets have different resolutions
    const resolutions: Record<string, number> = {
      temperature: 0.01,  // MUR SST: 0.01° resolution
      chlorophyll: 0.04,  // VIIRS: 4km ≈ 0.04°
      salinity: 0.25,     // SMAP: 0.25° resolution
    };
    const res = resolutions[parameter] || 0.1;
    const latRange = 40;  // -15 to 25 = 40°
    const lonRange = 50;  // 50 to 100 = 50°
    return Math.floor((latRange / res) * (lonRange / res));
  }, []);

  // NEW: Fetch real data from NOAA ERDDAP with zoom-based resolution
  const { data: erddapData, isLoading: erddapLoading, refetch: refetchErddap } = useQuery({
    queryKey: ['erddap-data', selectedParameter, zoomLevel],
    queryFn: async () => {
      const stride = getStrideForZoom(zoomLevel);
      const result = await erddapService.fetchByParameter(selectedParameter, {
        stride,
      });
      // Limit data points to prevent browser hanging
      const maxPoints = zoomLevel >= 7 ? 1000 : 500;
      const totalFetched = result.data?.length || 0;
      if (result.data && result.data.length > maxPoints) {
        result.data = result.data.slice(0, maxPoints);
      }
      // Add metadata for display
      (result as any).totalFetched = totalFetched;
      (result as any).stride = stride;
      (result as any).estimatedTotalCells = estimateTotalGridCells(selectedParameter);
      return result;
    },
    enabled: dataSourceMode === 'erddap' && selectedParameter !== 'fisheries_cpue',
    staleTime: 1000 * 60 * 30, // 30 min cache (refresh on zoom change)
    refetchOnWindowFocus: false,
  });

  // NEW: Fetch Fisheries CPUE spatial data
  const { data: fisheriesCpueData, isLoading: fisheriesCpueLoading } = useQuery({
    queryKey: ['fisheries-cpue-spatial'],
    queryFn: async () => {
      const res = await fetch(`${BACKEND_ROOT_URL}/api/fisheries/spatial-cpue`);
      return res.json();
    },
    enabled: selectedParameter === 'fisheries_cpue',
    staleTime: 1000 * 60 * 10, // 10 min cache for faster re-navigation
    refetchOnWindowFocus: false,
  });

  // NEW: Fetch Copernicus data for DO and pH (modeled biogeochemical data)
  const { data: copernicusData, isLoading: copernicusLoading, refetch: refetchCopernicus } = useQuery({
    queryKey: ['copernicus-data', selectedParameter, zoomLevel],
    queryFn: async () => {
      const stride = getStrideForZoom(zoomLevel);
      const result = await copernicusService.fetchByParameter(selectedParameter, {
        stride,
        depth: 0, // Surface only (0-5m) per implementation plan
      });
      // Limit data points for performance
      const maxPoints = zoomLevel >= 7 ? 1000 : 500;
      const totalFetched = result.data?.length || 0;
      if (result.data && result.data.length > maxPoints) {
        result.data = result.data.slice(0, maxPoints);
      }
      // Add metadata
      (result as any).totalFetched = totalFetched;
      (result as any).stride = stride;
      return result;
    },
    enabled: dataSourceMode === 'erddap' && (selectedParameter === 'dissolved_oxygen' || selectedParameter === 'pH'),
    staleTime: 1000 * 60 * 60, // 1 hour cache (monthly data)
    refetchOnWindowFocus: false,
  });

  // Toggle layer visibility
  const handleLayerToggle = useCallback((layer: VisibleLayer) => {
    setVisibleLayers(prev =>
      prev.includes(layer)
        ? prev.filter(l => l !== layer)
        : [...prev, layer]
    );
  }, []);

  // Handle data source change
  const handleDataSourceChange = useCallback((source: DataSourceMode) => {
    setDataSourceMode(source);
  }, []);

  // Generate animation frames based on satellite data availability
  // This creates a sequence of dates for the last 7 days
  useEffect(() => {
    if (dataSourceMode === 'erddap') {
      const frames: TimeFrame[] = [];
      const today = new Date();

      // Generate last 7 days of dates
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);

        const isoDate = date.toISOString().split('T')[0];
        const label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

        frames.push({
          date: isoDate,
          label,
          timestamp: date.getTime(),
        });
      }

      setAnimationFrames(frames);
      setAnimationFrameIndex(frames.length - 1); // Start at latest date
    } else {
      setAnimationFrames([]);
    }
  }, [dataSourceMode]);

  // Refresh data based on current source
  const handleRefresh = useCallback(() => {
    if (dataSourceMode === 'erddap') {
      refetchErddap();
    } else {
      refetch();
    }
  }, [dataSourceMode, refetchErddap]);

  // Fetch sources for filter dropdown
  const { data: sources } = useQuery({
    queryKey: ['oceanography-sources'],
    queryFn: () => oceanographyService.getSources(),
  });

  // Fetch oceanographic data with all filters
  const { data: oceanData, isLoading, refetch } = useQuery({
    queryKey: ['oceanography-data', selectedParameter, depthRange, dateRange, selectedSource],
    queryFn: () => oceanographyService.getData({
      parameter: selectedParameter,
      limit: 500,
      // Only pass depth filters if they're not at default values
      minDepth: depthRange[0] > 0 ? depthRange[0] : undefined,
      maxDepth: depthRange[1] < 500 ? depthRange[1] : undefined,
      startDate: dateRange.start || undefined,
      endDate: dateRange.end || undefined,
      source: selectedSource || undefined,
    }),
  });

  // Fetch parameters list
  const { data: parameters } = useQuery({
    queryKey: ['oceanography-parameters'],
    queryFn: () => oceanographyService.getParameters(),
  });

  // Fetch statistics
  const { data: stats } = useQuery({
    queryKey: ['oceanography-stats', selectedParameter],
    queryFn: () => oceanographyService.getStats({ parameter: selectedParameter }),
  });

  // Fetch time range
  const { data: timeRange } = useQuery({
    queryKey: ['oceanography-timerange'],
    queryFn: () => oceanographyService.getTimeRange(),
  });

  const dataPoints = oceanData?.data || [];

  // Calculate stats from filtered data (updates dynamically with filters)
  const currentStats = {
    count: dataPoints.length,
    avg_value: dataPoints.length > 0
      ? dataPoints.reduce((sum: number, p: any) => sum + (parseFloat(p.value) || 0), 0) / dataPoints.length
      : null,
    min_value: dataPoints.length > 0
      ? Math.min(...dataPoints.map((p: any) => parseFloat(p.value) || 0))
      : null,
    max_value: dataPoints.length > 0
      ? Math.max(...dataPoints.map((p: any) => parseFloat(p.value) || 0))
      : null,
    avg_depth: dataPoints.length > 0
      ? dataPoints.reduce((sum: number, p: any) => sum + (parseFloat(p.depth) || 0), 0) / dataPoints.length
      : null,
  };

  const toggleLayer = (id: string) => {
    setSelectedParameter(id);
    setLayers(prev => prev.map(layer => ({
      ...layer,
      enabled: layer.id === id
    })));
  };

  const handleRegionChange = (newRegion: string) => {
    setRegion(newRegion);
    switch (newRegion) {
      case 'arabian':
        setMapCenter([15, 65]);
        break;
      case 'bay':
        setMapCenter([15, 88]);
        break;
      case 'global':
        setMapCenter([0, 80]);
        break;
      default:
        setMapCenter([10, 76]);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <MapIcon className="w-5 h-5 text-ocean-500" />
            <span className="text-sm font-medium text-ocean-600 dark:text-ocean-400">GIS Platform</span>
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-deep-900 dark:text-white">Oceanography Viewer</h1>
          <p className="text-deep-500 dark:text-gray-400 mt-1">
            Interactive maps with real-time oceanographic parameters
          </p>
        </div>
        <div className="flex gap-3">
          <Select
            className="w-48"
            value={region}
            onChange={(e) => handleRegionChange(e.target.value)}
          >
            <option value="indian">Indian Ocean</option>
            <option value="arabian">Arabian Sea</option>
            <option value="bay">Bay of Bengal</option>
            <option value="global">Global View</option>
          </Select>
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" onClick={() => navigate('/reports')}>
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Horizontal Filters Bar & Data Source Control */}
      <Card variant="glass" className="p-4">
        <div className="flex flex-wrap items-center gap-6 w-full">
          {/* Conditional Content based on Source - Left Side */}
          {dataSourceMode === 'erddap' ? (
            /* Satellite Info Mode */
            <div className="flex items-center gap-3 animate-in fade-in duration-300 mr-auto">
              <div className="p-2 bg-ocean-50 rounded-full">
                <Satellite className="w-5 h-5 text-ocean-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-ocean-700 flex items-center gap-2">
                  NOAA Satellite Data
                  <Badge variant="secondary" className="bg-ocean-100 text-ocean-700 border-ocean-200 text-[10px] h-5">
                    Live
                  </Badge>
                </p>
                <p className="text-xs text-ocean-600">
                  Real-time observations from CoastWatch
                </p>
              </div>
            </div>
          ) : (
            /* Database Filters Mode */
            <div className="flex flex-wrap items-end gap-6 animate-in fade-in duration-300 mr-auto">
              {/* Depth Range */}
              <div className="flex-1 min-w-[280px] max-w-[360px]">
                <label className="text-xs font-medium text-deep-600 mb-1 flex items-center gap-2">
                  <Layers className="w-3 h-3 text-ocean-500" />
                  Depth: <span className="text-ocean-600 font-semibold">{depthRange[0]}m</span> - <span className="text-ocean-600 font-semibold">{depthRange[1]}m</span>
                </label>
                <div className="flex gap-2 items-center h-9 px-3 rounded-lg border border-gray-200 bg-white dark:bg-gray-900 dark:border-gray-700">
                  <input type="range" min="0" max="500" value={depthRange[0]}
                    onChange={(e) => setDepthRange([parseInt(e.target.value), depthRange[1]])}
                    className="flex-1 h-1.5 bg-gradient-to-r from-blue-200 to-ocean-300 rounded-lg appearance-none cursor-pointer accent-ocean-600" />
                  <input type="range" min="0" max="500" value={depthRange[1]}
                    onChange={(e) => setDepthRange([depthRange[0], parseInt(e.target.value)])}
                    className="flex-1 h-1.5 bg-gradient-to-r from-ocean-300 to-blue-500 rounded-lg appearance-none cursor-pointer accent-ocean-600" />
                </div>
              </div>

              {/* Source Filter */}
              <div>
                <label className="text-xs font-medium text-deep-600 mb-1 block">Source</label>
                <select value={selectedSource}
                  onChange={(e) => setSelectedSource(e.target.value)}
                  className="h-9 px-3 text-sm rounded-lg border border-gray-200 focus:border-ocean-400 focus:outline-none bg-white min-w-[140px]">
                  <option value="">All Sources</option>
                  {sources?.map((src: any) => (
                    <option key={src.source} value={src.source}>{src.source}</option>
                  ))}
                </select>
              </div>

              {/* Date Range */}
              <div className="flex gap-2 items-center">
                <div>
                  <label className="text-xs font-medium text-deep-600 mb-1 block">From</label>
                  <input type="date" value={dateRange.start}
                    onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                    className="h-9 px-2 text-sm rounded-lg border border-gray-200 w-32" />
                </div>
                <div>
                  <label className="text-xs font-medium text-deep-600 mb-1 block">To</label>
                  <input type="date" value={dateRange.end}
                    onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                    className="h-9 px-2 text-sm rounded-lg border border-gray-200 w-32" />
                </div>
              </div>
            </div>
          )}

          {/* Controls Group - Pushed to End */}
          <div className="flex items-center gap-4 ml-auto">
            {/* Clear Button */}
            <Button variant="outline" size="sm"
              onClick={() => { setDepthRange([0, 500]); setDateRange({ start: '', end: '' }); setSelectedSource(''); }}>
              Clear Filters
            </Button>

            <div className="h-8 w-px bg-gray-200 hidden sm:block"></div>

            {/* Data Source Toggle */}
            <div className="flex bg-gray-100 p-1 rounded-lg border border-gray-200">
              <button
                onClick={() => handleDataSourceChange('erddap')}
                className={cn(
                  "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all",
                  dataSourceMode === 'erddap'
                    ? "bg-white text-ocean-700 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                )}
              >
                <Satellite className="w-4 h-4" />
                Satellite
              </button>
              <button
                onClick={() => handleDataSourceChange('database')}
                className={cn(
                  "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all",
                  dataSourceMode === 'database'
                    ? "bg-white text-deep-700 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                )}
              >
                <Database className="w-4 h-4" />
                Database
              </button>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Map Container */}
        <div className="xl:col-span-3">
          <Card variant="default" className="overflow-hidden">
            <div className="relative h-[600px]">
              {(isLoading || (selectedParameter === 'fisheries_cpue' && fisheriesCpueLoading)) && (
                <div className="absolute inset-0 z-[1000] bg-white/80 dark:bg-gray-900/80 flex flex-col items-center justify-center">
                  <Loader className="w-8 h-8 animate-spin text-ocean-500" />
                  {selectedParameter === 'fisheries_cpue' && fisheriesCpueLoading && (
                    <p className="mt-3 text-sm text-deep-600 dark:text-gray-400">Loading Fisheries CPUE data...</p>
                  )}
                </div>
              )}

              <MapContainer
                center={mapCenter}
                zoom={5}
                style={{ height: '100%', width: '100%' }}
                className="z-0"
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapCenterControl center={mapCenter} />
                <ZoomChangeHandler onZoomChange={setZoomLevel} />

                {/* NASA OceanColor WMS Layer */}
                {visibleLayers.includes('nasa_wms') && (
                  <NASAOceanColorLayer
                    layer={selectedParameter === 'chlorophyll' ? 'chlorophyll' : 'sst'}
                    opacity={0.6}
                    visible={true}
                  />
                )}

                {/* Heatmap Layer for ERDDAP Data */}
                {visibleLayers.includes('heatmap') && erddapData?.data && (
                  <HeatmapLayer
                    data={erddapData.data.map(p => ({
                      latitude: p.latitude,
                      longitude: p.longitude,
                      value: p.value,
                    }))}
                    parameter={selectedParameter as 'temperature' | 'salinity' | 'chlorophyll'}
                    radius={30}
                    blur={20}
                    opacity={0.7}
                    visible={true}
                  />
                )}

                {/* Gridded Heatmap Layer - Professional grid visualization */}
                {visibleLayers.includes('gridded_heatmap') && erddapData?.data && (
                  <GriddedHeatmapLayer
                    data={erddapData.data.map(p => ({
                      latitude: p.latitude,
                      longitude: p.longitude,
                      value: p.value,
                    }))}
                    parameter={selectedParameter as 'temperature' | 'salinity' | 'chlorophyll'}
                    opacity={0.75}
                    visible={true}
                    zoomLevel={zoomLevel}
                  />
                )}

                {/* Argo BGC Float Layer - In-situ observations */}
                <ArgoFloatLayer
                  visible={visibleLayers.includes('argo_bgc')}
                  bounds={{
                    latMin: -15,
                    latMax: 25,
                    lonMin: 50,
                    lonMax: 100,
                  }}
                  onFloatClick={(floatId) => {
                    console.log('Clicked float:', floatId);
                    // Could open a detailed profile modal here
                  }}
                />

                {/* ERDDAP Real Data Markers */}
                {dataSourceMode === 'erddap' && visibleLayers.includes('markers') && erddapData?.data?.map((point, idx) => (
                  <CircleMarker
                    key={`erddap-${idx}`}
                    center={[point.latitude, point.longitude]}
                    radius={5}
                    pathOptions={{
                      fillColor: getParameterColor(selectedParameter, point.value),
                      fillOpacity: 0.8,
                      color: '#fff',
                      weight: 1
                    }}
                    eventHandlers={{
                      click: () => setSelectedPoint({
                        ...point,
                        source: 'NOAA_ERDDAP',
                        dataType: 'observed',
                      })
                    }}
                  >
                    <Tooltip direction="top" offset={[0, -5]} opacity={0.95}>
                      <div className="text-xs">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Satellite className="w-3 h-3 text-blue-500" />
                          <span className="font-semibold text-blue-600">NOAA ERDDAP</span>
                        </div>
                        <p className="text-sm font-bold">{formatNumber(point.value, 2)} {point.unit}</p>
                        <p className="text-gray-500 text-[10px]">Observed Data</p>
                      </div>
                    </Tooltip>
                  </CircleMarker>
                ))}

                {/* Copernicus Data Markers for DO/pH (Modeled Data) */}
                {dataSourceMode === 'erddap' && visibleLayers.includes('markers') &&
                  (selectedParameter === 'dissolved_oxygen' || selectedParameter === 'pH') &&
                  copernicusData?.data?.map((point: any, idx: number) => (
                    <CircleMarker
                      key={`copernicus-${idx}`}
                      center={[point.latitude, point.longitude]}
                      radius={5}
                      pathOptions={{
                        fillColor: getParameterColor(selectedParameter, point.value),
                        fillOpacity: 0.8,
                        color: '#fff',
                        weight: 1
                      }}
                      eventHandlers={{
                        click: () => setSelectedPoint({
                          ...point,
                          source: 'COPERNICUS',
                          dataType: 'modeled',
                        })
                      }}
                    >
                      <Tooltip direction="top" offset={[0, -5]} opacity={0.95}>
                        <div className="text-xs">
                          <div className="flex items-center gap-1.5 mb-1">
                            <Waves className="w-3 h-3 text-purple-500" />
                            <span className="font-semibold text-purple-600">Copernicus</span>
                            <span className="text-[10px] text-gray-400">(Monthly mean)</span>
                          </div>
                          <p className="text-sm font-bold">{formatNumber(point.value, 2)} {point.unit}</p>
                          <p className="text-gray-500 text-[10px]">Surface (0-5m) • Modeled</p>
                        </div>
                      </Tooltip>
                    </CircleMarker>
                  ))}

                {/* Database/Local Data Markers */}
                {dataSourceMode === 'database' && visibleLayers.includes('markers') && dataPoints.map((point: any, idx: number) => (
                  <CircleMarker
                    key={idx}
                    center={[point.latitude, point.longitude]}
                    radius={6}
                    pathOptions={{
                      fillColor: getParameterColor(selectedParameter, point.value),
                      fillOpacity: 0.8,
                      color: '#fff',
                      weight: 1
                    }}
                    eventHandlers={{
                      click: () => setSelectedPoint({
                        ...point,
                        source: 'DATABASE',
                        dataType: 'observed',
                      })
                    }}
                  >
                    <Popup>
                      <div className="text-sm">
                        <div className="flex items-center gap-1.5 mb-1">
                          <MapPin className="w-3 h-3 text-gray-500" />
                          <span className="font-medium text-gray-600">Local Database</span>
                        </div>
                        <p className="font-semibold">{point.parameter}</p>
                        <p className="text-lg font-bold">{formatNumber(point.value, 2)} {point.unit}</p>
                        <p className="text-gray-500">Depth: {point.depth}m</p>
                        <p className="text-gray-500">{new Date(point.timestamp).toLocaleDateString()}</p>
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}

                {/* Fisheries CPUE Markers - Show catch locations with CPUE intensity */}
                {selectedParameter === 'fisheries_cpue' && fisheriesCpueData?.data?.map((point: any, idx: number) => (
                  <CircleMarker
                    key={`cpue-${idx}`}
                    center={[point.lat, point.lon]}
                    radius={Math.max(6, Math.min(15, point.cpue * 2))} // Size based on CPUE
                    pathOptions={{
                      fillColor: getCpueColor(point.cpue),
                      fillOpacity: 0.75,
                      color: '#fff',
                      weight: 2
                    }}
                    eventHandlers={{
                      click: () => setSelectedPoint({
                        latitude: point.lat,
                        longitude: point.lon,
                        value: point.cpue,
                        unit: 'kg/hour',
                        parameter: 'CPUE',
                        totalCatch: point.totalCatch,
                        samples: point.samples,
                        depth: point.avgDepth,
                        source: 'FISHERIES',
                        dataType: 'observed',
                      })
                    }}
                  >
                    <Tooltip direction="top" offset={[0, -5]} opacity={0.95}>
                      <div className="text-xs">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Fish className="w-3 h-3 text-orange-500" />
                          <span className="font-semibold text-orange-600">Fisheries Data</span>
                        </div>
                        <p className="text-sm font-bold">CPUE: {point.cpue.toFixed(2)} kg/hour</p>
                        <p className="text-gray-600">Total Catch: {point.totalCatch.toFixed(1)} kg</p>
                        <p className="text-gray-500 text-[10px]">{point.samples} samples</p>
                      </div>
                    </Tooltip>
                  </CircleMarker>
                ))}

              </MapContainer>

              {/* Enhanced Legend with Source Attribution - Moved right to avoid zoom controls */}
              <div className="absolute top-2 left-16 z-[500]">
                <EnhancedLegend
                  parameter={selectedParameter}
                  unit={(() => {
                    switch (selectedParameter) {
                      case 'temperature': return '°C';
                      case 'salinity': return 'PSU';
                      case 'chlorophyll': return 'mg/m³';
                      case 'dissolved_oxygen': return 'mg/L';
                      case 'pH': return 'pH units';
                      default: return 'mg/m³';
                    }
                  })()}
                  min={(() => {
                    // Use Copernicus data for DO/pH, ERDDAP data for others
                    if (selectedParameter === 'dissolved_oxygen' || selectedParameter === 'pH') {
                      const copernicusPoints = copernicusData?.data?.filter((p: any) => p.value != null && !isNaN(p.value)) || [];
                      if (copernicusPoints.length > 0) {
                        return copernicusPoints.reduce((min: number, p: any) => p.value < min ? p.value : min, copernicusPoints[0].value);
                      }
                      return selectedParameter === 'dissolved_oxygen' ? 4 : 7.8; // Scientific defaults
                    }
                    const validData = erddapData?.data?.filter(p => p.value != null && !isNaN(p.value)) || [];
                    if (validData.length > 0) {
                      return validData.reduce((min, p) => p.value < min ? p.value : min, validData[0].value);
                    }
                    return currentStats.min_value || 0;
                  })()}
                  max={(() => {
                    // Use Copernicus data for DO/pH, ERDDAP data for others
                    if (selectedParameter === 'dissolved_oxygen' || selectedParameter === 'pH') {
                      const copernicusPoints = copernicusData?.data?.filter((p: any) => p.value != null && !isNaN(p.value)) || [];
                      if (copernicusPoints.length > 0) {
                        return copernicusPoints.reduce((max: number, p: any) => p.value > max ? p.value : max, copernicusPoints[0].value);
                      }
                      return selectedParameter === 'dissolved_oxygen' ? 8 : 8.3; // Scientific defaults
                    }
                    const validData = erddapData?.data?.filter(p => p.value != null && !isNaN(p.value)) || [];
                    if (validData.length > 0) {
                      return validData.reduce((max, p) => p.value > max ? p.value : max, validData[0].value);
                    }
                    return currentStats.max_value || 100;
                  })()}
                  colorScale={(() => {
                    switch (selectedParameter) {
                      case 'dissolved_oxygen': return 'dissolved_oxygen';
                      case 'pH': return 'pH';
                      default: return selectedParameter as 'temperature' | 'salinity' | 'chlorophyll';
                    }
                  })()}
                  source={(selectedParameter === 'dissolved_oxygen' || selectedParameter === 'pH') ? 'COPERNICUS' : dataSourceMode === 'erddap' ? 'NOAA_ERDDAP' : 'DATABASE'}
                  dataType={(selectedParameter === 'dissolved_oxygen' || selectedParameter === 'pH') ? 'modeled' : 'observed'}
                  dataPoints={(selectedParameter === 'dissolved_oxygen' || selectedParameter === 'pH') ? copernicusData?.data?.length : dataSourceMode === 'erddap' ? erddapData?.data?.length : dataPoints.length}
                  totalGridCells={(selectedParameter === 'dissolved_oxygen' || selectedParameter === 'pH') ? 12000 : 1250000}
                  stride={zoomLevel <= 3 ? 25 : zoomLevel <= 5 ? 10 : 5}
                  zoomLevel={zoomLevel}
                />
              </div>

              {/* Data Source Badge - Show Copernicus for DO/pH, ERDDAP for others */}
              {selectedParameter === 'dissolved_oxygen' || selectedParameter === 'pH' ? (
                <div className="absolute top-4 right-4 z-[500]">
                  <DataSourceBadge
                    source="COPERNICUS"
                    dataType="modeled"
                    parameter={selectedParameter}
                    lastUpdated={copernicusData?.metadata?.lastUpdated}
                    resolution="0.25°"
                    showDetails={true}
                  />
                </div>
              ) : (
                <div className="absolute top-4 right-4 z-[500]">
                  <DataSourceBadge
                    source={dataSourceMode === 'erddap' ? 'NOAA_ERDDAP' : 'DATABASE'}
                    dataType="observed"
                    parameter={selectedParameter}
                    lastUpdated={erddapData?.metadata?.lastUpdated}
                    resolution={erddapData?.metadata?.resolution}
                    showDetails={true}
                  />
                </div>
              )}
            </div>
          </Card>
        </div>



        {/* Side Panel */}
        <div className="space-y-4">
          {/* Temporal Animation Controls - Moved Here */}
          {dataSourceMode === 'erddap' && animationFrames.length > 1 && (
            <TemporalAnimationControl
              frames={animationFrames}
              currentFrameIndex={animationFrameIndex}
              onFrameChange={setAnimationFrameIndex}
              isLoading={erddapLoading}
            />
          )}
          {/* Parameters Panel */}
          <CollapsiblePanel title="Parameters" icon={Layers} defaultExpanded={false}>
            <div className="space-y-2">
              {layers
                .filter(layer => dataSourceMode === 'database' || layer.id !== 'fisheries_cpue')
                .map((layer) => (
                  <button
                    key={layer.id}
                    onClick={() => toggleLayer(layer.id)}
                    className={cn(
                      "w-full flex items-center gap-3 p-3 rounded-xl border transition-all",
                      layer.enabled
                        ? "border-ocean-200 bg-ocean-50"
                        : "border-gray-100 bg-white hover:bg-gray-50"
                    )}
                  >
                    <div className={cn(
                      "p-2 rounded-lg transition-colors",
                      layer.enabled ? "bg-ocean-100" : "bg-gray-100"
                    )}>
                      <layer.icon className={cn(
                        "w-4 h-4",
                        layer.enabled ? "text-ocean-600" : "text-gray-400"
                      )} />
                    </div>
                    <span className={cn(
                      "flex-1 text-left text-sm font-medium",
                      layer.enabled ? "text-ocean-700" : "text-deep-600"
                    )}>
                      {layer.name}
                    </span>
                    {layer.enabled ? (
                      <Eye className="w-4 h-4 text-ocean-500" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                ))}
            </div>
          </CollapsiblePanel>

          {/* Layer Controls (Moved from Map) */}
          <div className="z-10 relative">
            <LayerControl
              visibleLayers={visibleLayers}
              onLayerToggle={handleLayerToggle}
              onRefresh={handleRefresh}
              isLoading={isLoading || erddapLoading}
              dataPointCount={dataSourceMode === 'erddap' ? erddapData?.data?.length : dataPoints.length}
              totalGridCells={(erddapData as any)?.estimatedTotalCells}
              zoomLevel={zoomLevel}
              stride={(erddapData as any)?.stride}
              lastUpdated={erddapData?.timestamp || new Date().toISOString()}
              className="relative w-full shadow-sm border border-gray-200"
            />
          </div>


          {/* Selected Point Info */}
          {selectedPoint && (
            <Card variant="premium">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Data Point</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-deep-500">Parameter</span>
                    <span className="font-medium text-deep-900 capitalize">
                      {selectedPoint.parameter?.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-deep-500">Value</span>
                    <span className="font-bold text-lg text-ocean-600">
                      {formatNumber(selectedPoint.value, 2)} {selectedPoint.unit}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-deep-500">Coordinates</span>
                    <span className="font-mono text-xs text-deep-700">
                      {formatNumber(selectedPoint.latitude, 4)}, {formatNumber(selectedPoint.longitude, 4)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-deep-500">Depth</span>
                    <span className="font-medium text-deep-900">{selectedPoint.depth}m</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-deep-500">Quality</span>
                    <Badge variant={selectedPoint.quality === 'good' ? 'success' : 'warning'} dot>
                      {selectedPoint.quality}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-deep-500">Timestamp</span>
                    <span className="text-sm text-deep-700">
                      {new Date(selectedPoint.timestamp).toLocaleString()}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sources */}
          <Card variant="glass">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-ocean-50/60">
                  <Satellite className="w-5 h-5 text-ocean-400" />
                </div>
                <h4 className="text-sm font-semibold text-deep-900">Sources</h4>
              </div>
              {dataSourceMode === 'erddap' ? (
                <div className="space-y-2.5">
                  {[
                    { param: 'SST', source: 'NOAA CoastWatch', detail: 'JPL MUR · 1km · Daily' },
                    { param: 'Chlorophyll', source: 'NOAA VIIRS', detail: '4km · Monthly' },
                    { param: 'Salinity', source: 'NOAA SMAP', detail: '0.25° · Daily' },
                    { param: 'DO & pH', source: 'Copernicus Marine', detail: '0.25° · Monthly' },
                    { param: 'In-Situ', source: 'Argo BGC Floats', detail: 'Real-time Profiles' },
                    { param: 'CPUE', source: 'Fisheries Layer', detail: 'Database Records' },
                  ].map(({ param, source, detail }) => (
                    <div key={param} className="p-2 rounded-lg bg-white/60 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-deep-800 dark:text-gray-200">{param}</span>
                        <span className="text-[10px] text-ocean-500 font-medium">{detail}</span>
                      </div>
                      <p className="text-[11px] text-deep-500 dark:text-gray-400 mt-0.5">{source}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-1">
                  {parameters?.slice(0, 5).map((param: any) => (
                    <div key={param.parameter} className="flex justify-between text-xs p-1.5">
                      <span className="text-deep-600 capitalize">{param.parameter?.replace('_', ' ')}</span>
                      <span className="text-deep-400">{param.count} pts</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Global Data Source Disclaimer */}
          <div className="text-[10px] text-gray-400 text-center px-2 py-2 border-t border-gray-100">
            <p>Not all parameters are satellite-derived. Some variables are provided as modeled or in-situ validated products.</p>
            <p className="mt-1 text-gray-300">Data: E.U. Copernicus Marine Service • NOAA CoastWatch</p>
          </div>
        </div>
      </div >
    </div >
  );
}
